import { ApolloError } from '@apollo/client';
import React, { FC, useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import DOMPurify from 'dompurify';
import { APP_URLS, locale } from '../../../consts';
import {
  MagicxDataGetBySlugQuery,
  useMagicxDataGetBySlugLazyQuery,
} from '../../../libs/graphql/betzoneDirectusAPI/queries/__generated__/get-magicx-data-by-slug.query.generated';
import { activePromoStatuses, ParsedPromo, RewardPromo } from '../../Layout/Promotions/types';
import { getImageUrl } from '../../../utils/navigation';
import { isProgressBar, showCurrency, showPrimaryPromoCTA } from '../../Layout/Promotions/utils';
import { mockWage, mockWageRequired, PROMOTION } from '../../Layout/Promotions/consts';
import { Endpoint } from '../../../models/api.model';
import { PromotionError } from '../../Layout/Promotions/PromotionError';
import { Spinner } from '../../shared/Spinner';
import { Button } from '../../shared/Button';
import { ProgressBar } from '../../shared/ProgressBar';
import { CardCTASection } from '../../Layout/Promotions/PromotionCard/CardCTASection';
import { useFetchPlatformPromos } from '../../../hooks/useFetchPlatformPromos';
import './ItvPromo.scss';
import { evaluatePromosWithPlatform, isAnyPlatformPromoReceived } from '../ItvRowCollection/utils/platformPromos';
import BackArrowIcon from './../../../assets/Itv/BackArrow.svg';
import { ItvRecommendedGames } from '../ItvRecommendedGames';
import { HandleClaimClick } from '../../../layouts/pages/ItvGenericPage';
import { MediaBreakpoint, useBreakpointInfo } from '../../../hooks/useDeviceInfo';
import { useUserContext } from '../../../providers/UserProvider';
import { BRANDS } from '../types';
import { usePromoClaim } from '../../../hooks/usePromoClaim';
import { useReferralUrl } from '../../../hooks/useReferralUrl';
import { ReferralCopyInput } from '../ReferralCopyInput';

export const ItvPromo: FC = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const location = useLocation();
  const platformPromos = useFetchPlatformPromos();
  const sanitizer = DOMPurify.sanitize;
  const { pathname: pageUrl, hash } = location;

  const isMobile = useBreakpointInfo([MediaBreakpoint.MOBILE]);

  const isTermsPage = hash === APP_URLS.terms;
  const promotionSlug = String(useParams().id);

  const [directusMagicXPromotion, setDirectusMagicXPromotion] = useState<MagicxDataGetBySlugQuery | undefined>();
  const [resultPromotion, setResultPromotion] = useState<ParsedPromo | null>(null);
  const [handleClaimClick, setHandleClaimClick] = useState<HandleClaimClick>(() => () => {});

  usePromoClaim({ setHandleClaimClick });

  const promoStatus = String(resultPromotion?.status);

  const onClaimButtonClick = (): void => handleClaimClick({ ...parsedPromo, ...resultPromotion });

  const isActive = activePromoStatuses.includes(promoStatus);
  const promotion = directusMagicXPromotion?.magicx_promotions[0];
  const { isPromosLoading, promosLoadingError } = platformPromos;

  const {
    id,
    bonus_ID: bonusId,
    magicx_slug: magicXSlug,
    start_date: startDate,
    start_time: startTime,
    end_date: endDate,
    end_time: endTime,
    translations,
    eligible_games: eligibleGames,
    DM_Template_ID: magicDmBonusId,
    DM_Max_Amount: magicDmMaxAmount,
    bonus_type: promoType,
    thumbnail,
    thumbnail_wide,
    prize_refer,
    thumbnail_hero_mobile,
    reward_ID: rewardId,
    reward_type: rewardType,
    tag_ID: tagId,
  } = (promotion as typeof promotion & RewardPromo) || {};

  const resTransl = translations?.[0];
  const title = resTransl?.title || '';
  const subtitle = resTransl?.subtitle || '';
  const description = resTransl?.description || '';
  const imgPath = isMobile
    ? getImageUrl(thumbnail_hero_mobile?.filename_disk)
    : getImageUrl(thumbnail_wide?.filename_disk);
  const imgAlt = thumbnail?.title || PROMOTION;
  const terms = resTransl?.terms;
  const howToClaim = resTransl?.how_to_claim;
  const eligibleCustomers = resTransl?.eligible_customers;
  const parsedPromo = {
    id,
    bonusId,
    rewardId,
    magicXSlug,
    startDate: startDate && startTime ? `${startDate}T${startTime}` : startDate || '',
    endDate: endDate && endTime ? `${endDate}T${endTime}` : endDate || '',
    title,
    description,
    imgPath,
    imgAlt,
    pageUrl,
    hideTerms: isTermsPage,
    dmMaxAmount: magicDmMaxAmount,
    dmBonusId: magicDmBonusId,
    promoType: promoType || rewardType,
    ctaCopy: translations?.[0]?.cta_copy,
    ctaUrl: translations?.[0]?.cta_url,
    tagId,
    status: resultPromotion?.status,
    prizeRefer: Boolean(prize_refer),
  };

  const SUMMARY_DATA_TITLES = [
    t('promotions.details.summary.eligible-customers'),
    t('promotions.details.summary.how-to-claim'),
  ];

  const showCTA = showPrimaryPromoCTA(resultPromotion) && resultPromotion && !prize_refer;
  const isPrizeRefer = Boolean(prize_refer);
  const referralUrl = useReferralUrl();
  const showProgressBar = isProgressBar(resultPromotion?.status, parsedPromo.promoType);

  const wageNumber = Number(resultPromotion?.wageredAmount ?? mockWage);
  const wageReqNumber = Number(resultPromotion?.wageringRequirementAmount ?? mockWageRequired);

  const [fetchMagicXDetailsBySlug, { error: fetchMagicXDetailsBySlugError, loading: promoDetailsLoading }] =
    useMagicxDataGetBySlugLazyQuery({
      variables: {
        language: locale,
        slugs: [promotionSlug],
      },
      onCompleted: setDirectusMagicXPromotion,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      onError: (e: ApolloError): void => console.error(e.message),
      context: { endpoint: Endpoint.betzoneDirectusAPI },
    });

  useEffect(() => {
    (async (): Promise<void> => {
      await fetchMagicXDetailsBySlug();
    })();
  }, []);

  useEffect(() => {
    if (!isPromosLoading) {
      const isPlatformPromoReceived = isAnyPlatformPromoReceived(platformPromos);

      if (isPlatformPromoReceived) {
        const compiledPromo = evaluatePromosWithPlatform(parsedPromo, platformPromos);

        compiledPromo && setResultPromotion(compiledPromo);
      }
    }
  }, [isPromosLoading]);

  const isPromoLoading = promoDetailsLoading || isPromosLoading;
  const showError = isPromoLoading && !directusMagicXPromotion;
  const loadingError = promosLoadingError || fetchMagicXDetailsBySlugError;

  const { brand } = useParams();
  const { setTheme } = useUserContext();

  useEffect(() => {
    if (brand === BRANDS.BINGO || brand === BRANDS.SPINS) {
      setTheme(brand);
    }
  }, [brand, setTheme]);

  return (
    <>
      <div className="promo-main-wrapper">
        {isPromoLoading ? (
          <Spinner />
        ) : showError ? (
          <PromotionError type={loadingError ? 'loading' : 'not-found'} />
        ) : (
          <>
            <div className="promo-main-wrapper__back-button">
              <Button
                type="button"
                size="small"
                icon={<img src={BackArrowIcon} alt="icon" />}
                iconPosition="left"
                variant="transparent"
                style={{ width: '4rem', padding: '0', justifyContent: 'flex-start' }}
                onClick={(): void => navigate(-1)}
              >
                {t('promotions.button.back.text')}
              </Button>
            </div>

            <div className="promo-main-wrapper__content">
              <div
                className="promo-main-wrapper__promo-image"
                style={{
                  backgroundImage: `url(${imgPath})`,
                }}
              />

              <div className="promo-main-wrapper__details-section">
                <div className="promo-main-wrapper__details-row">
                  <div className="promo-main-wrapper__details-content">
                    {showProgressBar && (
                      <ProgressBar
                        showCurrency={showCurrency(resultPromotion?.type)}
                        value={wageNumber}
                        min={0}
                        max={wageReqNumber}
                        type="success"
                        className="promo-main-wrapper__progress-bar"
                        showWageringInfo
                      />
                    )}

                    <h1 className="promo-main-wrapper__details-title">{title}</h1>

                    {subtitle && <h1 className="promo-main-wrapper__details-subtitle">{subtitle}</h1>}

                    <div className="promo-main-wrapper__disclaimers">
                      <p
                        dangerouslySetInnerHTML={{
                          __html: sanitizer(description),
                        }}
                      />
                    </div>

                    {showCTA && (
                      <CardCTASection
                        onClaimClick={onClaimButtonClick}
                        showSecondary={false}
                        isActivePromotions={isActive}
                        promo={resultPromotion}
                      />
                    )}

                    {isPrizeRefer && referralUrl && (
                      <ReferralCopyInput
                        referralUrl={referralUrl}
                        wrapperClassName="promo-main-wrapper__referral-input"
                        inputBoxClassName="promo-main-wrapper__referral-input-field"
                        copyIconClassName="promo-main-wrapper__copy-icon"
                        copiedTextClassName="promo-main-wrapper__referral-copied"
                      />
                    )}
                  </div>
                  {(eligibleCustomers || howToClaim) && (
                    <div className="promo-main-wrapper__summary">
                      {[eligibleCustomers, howToClaim].map(
                        (item, index) =>
                          item && (
                            <div key={index} className="promo-main-wrapper__summary-item">
                              <h4 className="promo-main-wrapper__summary-title">{SUMMARY_DATA_TITLES[index]}:</h4>
                              <p
                                className="promo-main-wrapper__summary-text"
                                dangerouslySetInnerHTML={{
                                  __html: sanitizer(item),
                                }}
                              />
                            </div>
                          )
                      )}
                    </div>
                  )}
                </div>
                {Boolean(eligibleGames?.length) && (
                  <ItvRecommendedGames
                    eligibleGames={eligibleGames}
                    title={t('promotions.details.eligible-games.title')}
                  />
                )}
                {terms && (
                  <div className="promo-main-wrapper__terms">
                    <h3>- {t('promotions.details.terms.title')}</h3>
                    <div className="promo-main-wrapper__terms-content">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: sanitizer(terms),
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ItvPromo;
